<template>
  <div class="knowledge-graph">
    <TopBar :user="user" :apps="apps" />
    <!-- 页面头部 -->
    <div class="page-header">
      <h1>知识图谱系统</h1>
      <div class="header-actions">
        <button @click="showSettings = true" class="btn btn-secondary">
          设置
        </button>
        <button @click="showHelp = true" class="btn btn-secondary">
          帮助
        </button>

        <button @click="showToolbar = !showToolbar" class="btn btn-secondary">
          {{ showToolbar ? '隐藏工具栏' : '显示工具栏' }}
        </button>
        <button @click="toggleFullscreen" class="btn btn-secondary">
          {{ isFullscreen ? '退出全屏' : '全屏显示' }}
        </button>
        <button @click="exportData" class="btn btn-primary">导出数据（待实现）</button>
        <button @click="showImportModal = true" class="btn btn-outline">导入数据（待实现）</button>
        <button @click="clearAllData" class="btn btn-danger">清空数据</button>
      </div>
    </div>

    <!-- 主要内容区域 -->
    <div class="main-content">
      <!-- 左侧工具栏 -->
      <div v-if="showToolbar" class="sidebar left-sidebar">
        <GraphToolbar ref="graphToolbar" :selected-node="selectedNode" @add-node="handleAddNode"
          @add-edge="handleAddEdge" @add-group="handleAddGroup" @filter-change="handleFilterChange"
          @search="handleSearch" />
      </div>

      <!-- 中央图形区域 -->
      <div class="main-area">
        <div class="graph-container-wrapper">
          <KnowledgeGraphCanvas v-if="!is3DMode" ref="graphCanvas" @node-click="handleNodeClick"
            @edge-click="handleEdgeClick" @canvas-click="handleCanvasClick" @node-drag="handleNodeDrag"
            @node-edit="handleNodeEdit" @edge-edit="handleEdgeEdit" @node-lock="handleNodeLock"
            @node-highlight="handleNodeHighlight" @add-relationship="handleAddRelationship" />
          <KnowledgeGraph3D v-else ref="graph3D" @node-click="handleNodeClick" @edge-click="handleEdgeClick"
            @canvas-click="handleCanvasClick" @node-drag="handleNodeDrag" @node-edit="handleNodeEdit"
            @edge-edit="handleEdgeEdit" @node-lock="handleNodeLock" @node-highlight="handleNodeHighlight"
            @add-relationship="handleAddRelationship" />
        </div>

        <!-- 图形操作面板 -->
        <div class="graph-controls">
          <button @click="resetLayout" class="control-btn" title="重置布局">
            🔄
          </button>
          <button @click="fitView" class="control-btn" title="适应视图">
            🔍
          </button>
          <button @click="zoomIn" class="control-btn" title="放大">
            ➕
          </button>
          <button @click="zoomOut" class="control-btn" title="缩小">
            ➖
          </button>
          <button @click="toggleViewMode" class="control-btn" :title="is3DMode ? '切换到2D视图' : '切换到3D视图'">
            {{ is3DMode ? '2D' : '3D' }}
          </button>
          <button @click="toggleFullscreen" class="control-btn" title="全屏">
            {{ isFullscreen ? '⇲' : '⇱' }}
          </button>
        </div>
      </div>

      <!-- 右侧属性面板 -->
      <div class="sidebar right-sidebar">
        <PropertyPanel :selected-node="selectedNode" :selected-edge="selectedEdge" :groups="groups"
          @update-node="handleUpdateNode" @update-edge="handleUpdateEdge" @delete-node="handleDeleteNode"
          @delete-edge="handleDeleteEdge" @save-node-position="handleSaveNodePosition" />
      </div>
    </div>

    <!-- 数据导入模态框 -->
    <div v-if="showImportModal" class="modal-overlay" @click="showImportModal = false">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>导入数据</h3>
          <button @click="showImportModal = false" class="close-btn">&times;</button>
        </div>
        <div class="modal-body">
          <textarea v-model="importDataText" placeholder="请粘贴JSON格式的图数据..." rows="10"
            class="import-textarea"></textarea>
        </div>
        <div class="modal-footer">
          <button @click="showImportModal = false" class="btn btn-secondary">取消</button>
          <button @click="confirmImportData" class="btn btn-primary">导入</button>
        </div>
      </div>
    </div>

    <!-- 设置模态框 -->
    <div v-if="showSettings" class="modal-overlay" @click="showSettings = false">
      <div class="modal-content settings-modal" @click.stop>
        <div class="modal-header">
          <h3>图形设置</h3>
          <button @click="showSettings = false" class="close-btn">&times;</button>
        </div>
        <div class="modal-body">
          <div class="settings-group">
            <h4>布局设置</h4>
            <div class="setting-item">
              <label>
                默认排布模式:
                <select v-model="graphSettings.layoutType">
                  <option value="none">无布局</option>
                  <option value="tree">树形布局</option>
                  <option value="force">力导向布局</option>
                  <option value="circular">环形布局（待完善）</option>
                  <option value="grid">网格布局（待完善）</option>
                </select>
              </label>
            </div>
            <!-- 添加树形布局方向设置 -->
            <div class="setting-item" v-if="graphSettings.layoutType === 'tree'">
              <label>
                树形布局方向:
                <select v-model="graphSettings.treeDirection">
                  <option value="left-to-right">自左向右</option>
                  <option value="top-to-bottom">自上而下</option>
                  <option value="right-to-left">自右向左</option>
                  <option value="bottom-to-top">自下而上</option>
                </select>
              </label>
            </div>
            <div class="setting-item">
              <label>
                <input type="checkbox" v-model="graphSettings.autoLayout" />
                自动执行引力效果
              </label>
            </div>
          </div>
          <div class="settings-group">
            <h4>交互设置（暂无）</h4>
          </div>
          <div class="settings-group">
            <h4>显示设置（暂无）</h4>
          </div>
          <!-- 关系线样式设置 -->
          <div class="settings-group">
            <h4>关系线样式设置</h4>
            <div class="setting-item">
              <label>
                线条样式:
                <select v-model="graphSettings.edgeStyle">
                  <option value="straight">直线</option>
                  <option value="dashed">虚线</option>
                  <option value="curve">曲线</option>
                  <option value="orthogonal">正交线</option>
                  <option value="bezier">贝塞尔线</option>

                </select>
              </label>
            </div>
            <div class="setting-item">
              <label>
                线条颜色方案:
                <select v-model="graphSettings.edgeColorScheme">
                  <option value="default">默认</option>
                  <option value="gradient">渐变色</option>
                  <option value="category">分类色</option>
                  <option value="random">随机色</option>
                </select>
              </label>
            </div>
            <div class="setting-item">
              <label>
                线条粗细:
                <input type="range" v-model="graphSettings.edgeWidth" min="1" max="10" step="0.5" />
                {{ graphSettings.edgeWidth }}px
              </label>
            </div>
            <div class="setting-item">
              <label>
                <input type="checkbox" v-model="graphSettings.showArrow" />
                显示箭头
              </label>
            </div>
            <div class="setting-item" v-if="graphSettings.showArrow">
              <label>
                箭头样式:
                <select v-model="graphSettings.arrowStyle">
                  <option value="default">默认</option>
                  <option value="hollow">空心三角</option>
                  <option value="circle">圆形</option>
                  <option value="diamond">菱形</option>
                </select>
              </label>
            </div>
            <div class="setting-item">
              <label>
                <input type="checkbox" v-model="graphSettings.enableGlow" />
                启用发光效果
              </label>
            </div>
            <div class="setting-item">
              <label>
                <input type="checkbox" v-model="graphSettings.enableAnimation" />
                启用动画效果
              </label>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="showSettings = false" class="btn btn-secondary">关闭</button>
          <button @click="applySettings" class="btn btn-primary">应用设置</button>
        </div>
      </div>
    </div>

    <!-- 帮助弹窗 -->
    <div v-if="showHelp" class="modal-overlay help-overlay" @click="showHelp = false">
      <div class="modal-content help-modal" @click.stop>
        <div class="modal-header">
          <h3>知识图谱操作说明</h3>
          <button @click="showHelp = false" class="close-btn">&times;</button>
        </div>
        <div class="modal-body help-content">
          <!-- 可折叠的帮助内容 -->
          <div class="help-accordion">
            <!-- 界面操作 -->
            <div class="help-section">
              <div class="help-section-header" @click="toggleHelpSection('interface')">
                <h4>界面操作</h4>
                <span class="help-toggle-icon">{{ openHelpSections.interface ? '−' : '+' }}</span>
              </div>
              <div v-show="openHelpSections.interface" class="help-section-content">
                <div class="help-subsection">
                  <div class="help-subsection-header" @click="toggleHelpSubsection('interface', 'search')">
                    <h5>查找节点（待完善）</h5>
                    <span class="help-toggle-icon">{{ openHelpSubsections.interface?.search ? '−' : '+' }}</span>
                  </div>
                  <div v-show="openHelpSubsections.interface?.search" class="help-subsection-content">
                    <ul>
                      <li>（待完善）</li>

                    </ul>
                  </div>
                </div>

                <div class="help-subsection">
                  <div class="help-subsection-header" @click="toggleHelpSubsection('interface', 'expand')">
                    <h5>拓展节点</h5>
                    <span class="help-toggle-icon">{{ openHelpSubsections.interface?.expand ? '−' : '+' }}</span>
                  </div>
                  <div v-show="openHelpSubsections.interface?.expand" class="help-subsection-content">
                    <ul>
                      <li>点击任意<span class="slot from-node node-value">节点</span>后，系统会自动查找一定数量的与<span
                          class="slot from-node node-value">节点</span>相关的数据显示出来</li>
                    </ul>
                  </div>
                </div>

                <div class="help-subsection">
                  <div class="help-subsection-header" @click="toggleHelpSubsection('interface', 'link')">
                    <h5>绑定关系</h5>
                    <span class="help-toggle-icon">{{ openHelpSubsections.interface?.link ? '−' : '+' }}</span>
                  </div>
                  <div v-show="openHelpSubsections.interface?.link" class="help-subsection-content">
                    <h6>右击节点快捷添加(界面操作)</h6>
                    <ul>
                      <li>点击选择一个节点作为<span class="slot from-node node-value">源节点</span></li>
                      <li>右键点击另一个节点作为【目标节点】</li>
                      <li>如果提示中选择【关系类型】则需要在左侧工具栏中选择</li>
                      <li>如果没有提示，你会看到系统将自动创建从源节点到目标节点的关系</li>
                    </ul>

                  </div>
                </div>

                <div class="help-subsection">
                  <div class="help-subsection-header" @click="toggleHelpSubsection('interface', 'cutlink')">
                    <h5>解绑关系</h5>
                    <span class="help-toggle-icon">{{ openHelpSubsections.interface?.cutlink ? '−' : '+' }}</span>
                  </div>
                  <div v-show="openHelpSubsections.interface?.cutlink" class="help-subsection-content">
                    <h6>右击划线删除</h6>
                    <ul>
                      <li>在界面中右击空白处，在鼠标处会有一个黄色的小圆点</li>
                      <li>此时进入了【划线状态】，鼠标移动，会划出一条黄色的线</li>
                      <li>此状态下划到任意一条关系线，都会提示是否删除</li>
                      <li>无论是否删除，都会直接退出【划线状态】</li>
                      <li>右击双击，即可退出【划线状态】</li>
                    </ul>

                  </div>
                </div>

                <div class="help-subsection">
                  <div class="help-subsection-header" @click="toggleHelpSubsection('interface', 'group')">
                    <h5>群组管理</h5>
                    <span class="help-toggle-icon">{{ openHelpSubsections.interface?.group ? '−' : '+' }}</span>
                  </div>
                  <div v-show="openHelpSubsections.interface?.group" class="help-subsection-content">
                    <h6>右击管理群组</h6>
                    <ul>
                      <li>群组管理只是临时数据，不会影响数据库</li>
                      <li>右击节点，在弹出的【右击菜单】中找到【群组管理】</li>
                      <li>鼠标指向【群组管理】后，会弹出【群组列表】</li>
                      <li>【群组列表】中蓝色为已经加入的群组，灰色为未加入的群组</li>
                      <li>点击对应的群组，就会自动在加入和未加入之间进行切换</li>
                      <li>你可以在顶部菜单的【群组标签】中选择查看对应的群组</li>
                    </ul>

                  </div>
                </div>

                <div class="help-subsection">
                  <div class="help-subsection-header" @click="toggleHelpSubsection('interface', 'other')">
                    <h5>其他操作</h5>
                    <span class="help-toggle-icon">{{ openHelpSubsections.interface?.other ? '−' : '+' }}</span>
                  </div>
                  <div v-show="openHelpSubsections.interface?.other" class="help-subsection-content">
                    <ul>
                      <li>拖拽节点可以调整其位置</li>
                      <li>使用图形操作面板中的按钮可以缩放、重置布局等（待完成）</li>

                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <!-- 工具栏操作 -->
            <div class="help-section">
              <div class="help-section-header" @click="toggleHelpSection('toolbar')">
                <h4>工具栏操作</h4>
                <span class="help-toggle-icon">{{ openHelpSections.toolbar ? '−' : '+' }}</span>
              </div>
              <div v-show="openHelpSections.toolbar" class="help-section-content">
                <div class="help-subsection">
                  <div class="help-subsection-header" @click="toggleHelpSubsection('toolbar', 'node')">
                    <h5>节点操作</h5>
                    <span class="help-toggle-icon">{{ openHelpSubsections.toolbar?.node ? '−' : '+' }}</span>
                  </div>
                  <div v-show="openHelpSubsections.toolbar?.node" class="help-subsection-content">
                    <div class="help-subsection">
                      <div class="help-subsection-header" @click="toggleHelpSubsection('toolbar', 'findNode')">
                        <h6>查询节点</h6>
                        <span class="help-toggle-icon">{{ openHelpSubsections.toolbar?.findNode ? '−' : '+' }}</span>
                      </div>
                      <div v-show="openHelpSubsections.toolbar?.findNode" class="help-subsection-content">

                        <ul>
                          <li>在左侧工具栏输入【节点名称】</li>
                          <li>系统会自动将模糊匹配的节点突出显示</li>
                          <li>删除【节点名称】内容后会自动取消突出显示</li>
                        </ul>

                      </div>
                    </div>

                    <div class="help-subsection">
                      <div class="help-subsection-header" @click="toggleHelpSubsection('toolbar', 'addNewNode')">
                        <h6>添加新节点</h6>
                        <span class="help-toggle-icon">{{ openHelpSubsections.toolbar?.addNewNode ? '−' : '+' }}</span>
                      </div>
                      <div v-show="openHelpSubsections.toolbar?.addNewNode" class="help-subsection-content">

                        <ul>
                          <li>在左侧工具栏输入【节点名称】和【节点描述】</li>
                          <li>点击【API添加节点】按钮添加新节点</li>
                        </ul>

                      </div>
                    </div>

                    <div class="help-subsection">
                      <div class="help-subsection-header" @click="toggleHelpSubsection('toolbar', 'addNode')">
                        <h6>数据查询添加</h6>
                        <span class="help-toggle-icon">{{ openHelpSubsections.toolbar?.addNode ? '−' : '+' }}</span>
                      </div>
                      <div v-show="openHelpSubsections.toolbar?.addNode" class="help-subsection-content">

                        <ul>
                          <li>【节点名称】中输入内容</li>
                          <li>系统会自动将匹配的节点突出显示</li>
                          <li>同时系统会在数据库中进行【精确匹配】和【模糊匹配】</li>
                          <li>【精确匹配】的数据显示为蓝色</li>
                          <li>【模糊匹配】的数据显示为黄色</li>
                          <li>点击匹配出来的节点，系统会自动将此节点添加到界面中</li>
                        </ul>
                      </div>
                    </div>

                    <div class="help-subsection">
                      <div class="help-subsection-header" @click="toggleHelpSubsection('toolbar', 'editNode')">
                        <h6>编辑节点（待完成）</h6>
                        <span class="help-toggle-icon">{{ openHelpSubsections.toolbar?.editNode ? '−' : '+' }}</span>
                      </div>
                      <div v-show="openHelpSubsections.toolbar?.editNode" class="help-subsection-content">
                        <ul>
                          <li>双击节点可以编辑节点标签</li>
                          <li>在右侧属性面板中可以修改节点的其他属性</li>
                        </ul>
                      </div>
                    </div>

                    <div class="help-subsection">
                      <div class="help-subsection-header" @click="toggleHelpSubsection('toolbar', 'deleteNode')">
                        <h6>删除节点（待完成）</h6>
                        <span class="help-toggle-icon">{{ openHelpSubsections.toolbar?.deleteNode ? '−' : '+' }}</span>
                      </div>
                      <div v-show="openHelpSubsections.toolbar?.deleteNode" class="help-subsection-content">
                        <ul>
                          <li>点击选择要删除的节点</li>
                          <li>在右侧属性面板中点击相应的删除按钮</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="help-subsection">
                  <div class="help-subsection-header" @click="toggleHelpSubsection('toolbar', 'relationship')">
                    <h5>关系操作</h5>
                    <span class="help-toggle-icon">{{ openHelpSubsections.toolbar?.relationship ? '−' : '+' }}</span>
                  </div>
                  <div v-show="openHelpSubsections.toolbar?.relationship" class="help-subsection-content">
                    <div class="help-subsection">
                      <div class="help-subsection-header" @click="toggleHelpSubsection('toolbar', 'addRelationship')">
                        <h6>添加关系</h6>
                        <span class="help-toggle-icon">{{ openHelpSubsections.toolbar?.addRelationship ? '−' : '+'
                        }}</span>
                      </div>
                      <div v-show="openHelpSubsections.toolbar?.addRelationship" class="help-subsection-content">
                        <h6>左侧工具栏添加</h6>
                        <ul>
                          <li>点击选择一个节点作为<span class="slot from-node node-value">源节点</span></li>
                          <li>点击选择另一个节点作为【目标节点】</li>
                          <li>你也可以点击【切换】按钮，实现<span class="slot from-node node-value">源节点</span>与【目标节点】的切换</li>
                          <li>在左侧工具栏中下拉选择【关系类型】（只有在一个节点被选中的时候才会显示出来）</li>
                          <li>点击【节点绑定关系】即可进行关系绑定（只有在一个节点被选中的时候才会显示出来）</li>
                        </ul>

                        <h6>右击节点快捷添加(界面操作)</h6>
                        <ul>
                          <li>点击选择一个节点作为<span class="slot from-node node-value">源节点</span></li>
                          <li>右键点击另一个节点作为【目标节点】</li>
                          <li>如果提示中选择【关系类型】则需要在左侧工具栏中选择</li>
                          <li>如果没有提示，你会看到系统将自动创建从源节点到目标节点的关系</li>
                        </ul>
                      </div>
                    </div>

                    <div class="help-subsection">
                      <div class="help-subsection-header"
                        @click="toggleHelpSubsection('toolbar', 'deleteRelationship')">
                        <h6>删除关系（待完成）</h6>
                        <span class="help-toggle-icon">{{ openHelpSubsections.toolbar?.deleteRelationship ? '−' : '+'
                        }}</span>
                      </div>
                      <div v-show="openHelpSubsections.toolbar?.deleteRelationship" class="help-subsection-content">
                        <ul>
                          <li>点击选择要删除的关系</li>
                          <li>在右侧属性面板中点击相应的删除按钮</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <!-- 菜单栏操作 -->
            <div class="help-section">
              <div class="help-section-header" @click="toggleHelpSection('toolbar')">
                <h4>菜单栏操作（待开发）</h4>
                <span class="help-toggle-icon">{{ openHelpSections.toolbar ? '−' : '+' }}</span>
              </div>
              <div v-show="openHelpSections.toolbar" class="help-section-content">
                <div class="help-subsection">
                  <div class="help-subsection-header" @click="toggleHelpSubsection('toolbar', 'node')">
                    <h5>节点操作（待开发）</h5>
                    <span class="help-toggle-icon">{{ openHelpSubsections.toolbar?.node ? '−' : '+' }}</span>
                  </div>
                  <div v-show="openHelpSubsections.toolbar?.node" class="help-subsection-content">
                    <div class="help-subsection">
                      <div class="help-subsection-header" @click="toggleHelpSubsection('toolbar', 'findNode')">
                        <h6>查询节点</h6>
                        <span class="help-toggle-icon">{{ openHelpSubsections.toolbar?.findNode ? '−' : '+' }}</span>
                      </div>
                      <div v-show="openHelpSubsections.toolbar?.findNode" class="help-subsection-content">

                        <ul>
                          <li>在左侧工具栏输入【节点名称】</li>
                          <li>系统会自动将模糊匹配的节点突出显示</li>
                          <li>删除【节点名称】内容后会自动取消突出显示</li>
                        </ul>

                      </div>
                    </div>

                    <div class="help-subsection">
                      <div class="help-subsection-header" @click="toggleHelpSubsection('toolbar', 'addNewNode')">
                        <h6>添加新节点</h6>
                        <span class="help-toggle-icon">{{ openHelpSubsections.toolbar?.addNewNode ? '−' : '+' }}</span>
                      </div>
                      <div v-show="openHelpSubsections.toolbar?.addNewNode" class="help-subsection-content">

                        <ul>
                          <li>在左侧工具栏输入【节点名称】和【节点描述】</li>
                          <li>点击【API添加节点】按钮添加新节点</li>
                        </ul>

                      </div>
                    </div>

                    <div class="help-subsection">
                      <div class="help-subsection-header" @click="toggleHelpSubsection('toolbar', 'addNode')">
                        <h6>数据查询添加</h6>
                        <span class="help-toggle-icon">{{ openHelpSubsections.toolbar?.addNode ? '−' : '+' }}</span>
                      </div>
                      <div v-show="openHelpSubsections.toolbar?.addNode" class="help-subsection-content">

                        <ul>
                          <li>【节点名称】中输入内容</li>
                          <li>系统会自动将匹配的节点突出显示</li>
                          <li>同时系统会在数据库中进行【精确匹配】和【模糊匹配】</li>
                          <li>【精确匹配】的数据显示为蓝色</li>
                          <li>【模糊匹配】的数据显示为黄色</li>
                          <li>点击匹配出来的节点，系统会自动将此节点添加到界面中</li>
                        </ul>
                      </div>
                    </div>

                    <div class="help-subsection">
                      <div class="help-subsection-header" @click="toggleHelpSubsection('toolbar', 'editNode')">
                        <h6>编辑节点（待完成）</h6>
                        <span class="help-toggle-icon">{{ openHelpSubsections.toolbar?.editNode ? '−' : '+' }}</span>
                      </div>
                      <div v-show="openHelpSubsections.toolbar?.editNode" class="help-subsection-content">
                        <ul>
                          <li>双击节点可以编辑节点标签</li>
                          <li>在右侧属性面板中可以修改节点的其他属性</li>
                        </ul>
                      </div>
                    </div>

                    <div class="help-subsection">
                      <div class="help-subsection-header" @click="toggleHelpSubsection('toolbar', 'deleteNode')">
                        <h6>删除节点（待完成）</h6>
                        <span class="help-toggle-icon">{{ openHelpSubsections.toolbar?.deleteNode ? '−' : '+' }}</span>
                      </div>
                      <div v-show="openHelpSubsections.toolbar?.deleteNode" class="help-subsection-content">
                        <ul>
                          <li>点击选择要删除的节点</li>
                          <li>在右侧属性面板中点击相应的删除按钮</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

              </div>
            </div>
            <!-- 属性栏操作 -->
            <div class="help-section">
              <div class="help-section-header" @click="toggleHelpSection('toolbar')">
                <h4>属性栏操作（待开发）</h4>
                <span class="help-toggle-icon">{{ openHelpSections.toolbar ? '−' : '+' }}</span>
              </div>
              <div v-show="openHelpSections.toolbar" class="help-section-content">
                <div class="help-subsection">
                  <div class="help-subsection-header" @click="toggleHelpSubsection('toolbar', 'node')">
                    <h5>节点操作（待开发）</h5>
                    <span class="help-toggle-icon">{{ openHelpSubsections.toolbar?.node ? '−' : '+' }}</span>
                  </div>
                  <div v-show="openHelpSubsections.toolbar?.node" class="help-subsection-content">
                    <div class="help-subsection">
                      <div class="help-subsection-header" @click="toggleHelpSubsection('toolbar', 'findNode')">
                        <h6>查询节点</h6>
                        <span class="help-toggle-icon">{{ openHelpSubsections.toolbar?.findNode ? '−' : '+' }}</span>
                      </div>
                      <div v-show="openHelpSubsections.toolbar?.findNode" class="help-subsection-content">

                        <ul>
                          <li>在左侧工具栏输入【节点名称】</li>
                          <li>系统会自动将模糊匹配的节点突出显示</li>
                          <li>删除【节点名称】内容后会自动取消突出显示</li>
                        </ul>

                      </div>
                    </div>

                    <div class="help-subsection">
                      <div class="help-subsection-header" @click="toggleHelpSubsection('toolbar', 'addNewNode')">
                        <h6>添加新节点</h6>
                        <span class="help-toggle-icon">{{ openHelpSubsections.toolbar?.addNewNode ? '−' : '+' }}</span>
                      </div>
                      <div v-show="openHelpSubsections.toolbar?.addNewNode" class="help-subsection-content">

                        <ul>
                          <li>在左侧工具栏输入【节点名称】和【节点描述】</li>
                          <li>点击【API添加节点】按钮添加新节点</li>
                        </ul>

                      </div>
                    </div>

                    <div class="help-subsection">
                      <div class="help-subsection-header" @click="toggleHelpSubsection('toolbar', 'addNode')">
                        <h6>数据查询添加</h6>
                        <span class="help-toggle-icon">{{ openHelpSubsections.toolbar?.addNode ? '−' : '+' }}</span>
                      </div>
                      <div v-show="openHelpSubsections.toolbar?.addNode" class="help-subsection-content">

                        <ul>
                          <li>【节点名称】中输入内容</li>
                          <li>系统会自动将匹配的节点突出显示</li>
                          <li>同时系统会在数据库中进行【精确匹配】和【模糊匹配】</li>
                          <li>【精确匹配】的数据显示为蓝色</li>
                          <li>【模糊匹配】的数据显示为黄色</li>
                          <li>点击匹配出来的节点，系统会自动将此节点添加到界面中</li>
                        </ul>
                      </div>
                    </div>

                    <div class="help-subsection">
                      <div class="help-subsection-header" @click="toggleHelpSubsection('toolbar', 'editNode')">
                        <h6>编辑节点（待完成）</h6>
                        <span class="help-toggle-icon">{{ openHelpSubsections.toolbar?.editNode ? '−' : '+' }}</span>
                      </div>
                      <div v-show="openHelpSubsections.toolbar?.editNode" class="help-subsection-content">
                        <ul>
                          <li>双击节点可以编辑节点标签</li>
                          <li>在右侧属性面板中可以修改节点的其他属性</li>
                        </ul>
                      </div>
                    </div>

                    <div class="help-subsection">
                      <div class="help-subsection-header" @click="toggleHelpSubsection('toolbar', 'deleteNode')">
                        <h6>删除节点（待完成）</h6>
                        <span class="help-toggle-icon">{{ openHelpSubsections.toolbar?.deleteNode ? '−' : '+' }}</span>
                      </div>
                      <div v-show="openHelpSubsections.toolbar?.deleteNode" class="help-subsection-content">
                        <ul>
                          <li>点击选择要删除的节点</li>
                          <li>在右侧属性面板中点击相应的删除按钮</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>


              </div>
            </div>
            <!-- 相关知识 -->
            <div class="help-section">
              <div class="help-section-header" @click="toggleHelpSection('base')">
                <h4>数据体系</h4>
                <span class="help-toggle-icon">{{ openHelpSections.base ? '−' : '+' }}</span>
              </div>
              <div v-show="openHelpSections.base" class="help-section-content">
                <div class="help-subsection">
                  <div class="help-subsection-header" @click="toggleHelpSubsection('base', 'node')">
                    <h5>节点数据</h5>
                    <span class="help-toggle-icon">{{ openHelpSubsections.base?.node ? '−' : '+' }}</span>
                  </div>
                  <div v-show="openHelpSubsections.base?.node" class="help-subsection-content">
                    <div class="help-subsection">
                      <div class="help-subsection-header" @click="toggleHelpSubsection('base', 'nodeDesc')">
                        <h6>节点定义</h6>
                        <span class="help-toggle-icon">{{ openHelpSubsections.base?.nodeDesc ? '−' : '+' }}</span>
                      </div>
                      <div v-show="openHelpSubsections.base?.nodeDesc" class="help-subsection-content">

                        <ul>
                          <li>所有实际内容都应该是【节点】</li>
                          <li>标签和关系，都起源于节点，所以都有自己的【节点】</li>

                        </ul>

                      </div>
                    </div>


                  </div>
                </div>
                <div class="help-subsection">
                  <div class="help-subsection-header" @click="toggleHelpSubsection('base', 'graph')">
                    <h5>关系数据</h5>
                    <span class="help-toggle-icon">{{ openHelpSubsections.base?.graph ? '−' : '+' }}</span>
                  </div>
                  <div v-show="openHelpSubsections.base?.graph" class="help-subsection-content">
                    <div class="help-subsection">
                      <div class="help-subsection-header" @click="toggleHelpSubsection('base', 'tag')">
                        <h6>tag标签</h6>
                        <span class="help-toggle-icon">{{ openHelpSubsections.base?.tag ? '−' : '+' }}</span>
                      </div>
                      <div v-show="openHelpSubsections.base?.tag" class="help-subsection-content">
                        <ul>
                          <li>是graph和tag的基础，只有tag可以成为graph或tag使用</li>
                          <li class="relation-display">
                            <div class="relation-item">
                              <span class="relation-label">Tag</span>
                              <span class="relation-structure">
                                <span class="slot from-node null-value">fromNode=NULL</span>
                                <span class="connector">--</span>
                                <span class="slot graph null-value">Graph=NULL</span>
                                <span class="connector">--</span>
                                <span class="slot to-node node-value">toNode=Node</span>
                              </span>
                            </div>
                          </li>
                        </ul>
                      </div>
                    </div>
                    <div class="help-subsection">
                      <div class="help-subsection-header" @click="toggleHelpSubsection('base', 'bindTag')">
                        <h6>绑定标签</h6>
                        <span class="help-toggle-icon">{{ openHelpSubsections.base?.bindTag ? '−' : '+' }}</span>
                      </div>
                      <div v-show="openHelpSubsections.base?.bindTag" class="help-subsection-content">
                        <ul>
                          <li>仅将数据添加一个标签，不作为关系</li>
                          <li class="relation-display">
                            <div class="relation-item">
                              <span class="relation-label">TagGraph</span>
                              <span class="relation-structure">
                                <span class="slot from-node node-value">fromNode=Node</span>
                                <span class="connector">--</span>
                                <span class="slot graph tag-value">Graph=Tag</span>
                                <span class="connector">--</span>
                                <span class="slot to-node null-value">toNode=NULL</span>
                              </span>
                            </div>
                          </li>
                        </ul>
                      </div>
                    </div>

                    <div class="help-subsection">
                      <div class="help-subsection-header" @click="toggleHelpSubsection('base', 'graphData')">
                        <h6>graph数据</h6>
                        <span class="help-toggle-icon">{{ openHelpSubsections.base?.graphData ? '−' : '+' }}</span>
                      </div>
                      <div v-show="openHelpSubsections.base?.graphData" class="help-subsection-content">
                        <ul>
                          <li>是graph和tag的基础，只有tag可以成为graph或tag使用</li>
                          <li class="relation-display">
                            <div class="relation-item">
                              <span class="relation-label">Graph</span>
                              <span class="relation-structure">
                                <span class="slot from-node node-value">fromNode=Node</span>
                                <span class="connector">--</span>
                                <span class="slot graph tag-value">Graph=Tag</span>
                                <span class="connector">--</span>
                                <span class="slot to-node node-value">toNode=Node</span>
                              </span>
                            </div>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

              </div>
            </div>

          </div>
        </div>
        <div class="modal-footer">
          <button @click="showHelp = false" class="btn btn-primary">关闭</button>
        </div>
      </div>
    </div>

    <!-- 加载提示 -->
    <div v-if="isLoading" class="loading-overlay">
      <div class="loading-spinner">
        <div class="spinner"></div>
        <p>正在处理...</p>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, nextTick, reactive } from 'vue'
import { useKnowledgeGraphStore } from './stores/knowledgeGraphStore.js'
import KnowledgeGraphCanvas from './components/KnowledgeGraphCanvas.vue'
import KnowledgeGraph3D from './components/KnowledgeGraph3D.vue'
import GraphToolbar from './components/GraphToolbar.vue'
import PropertyPanel from './components/PropertyPanel.vue'
import TopBar from '@/components/TopBar.vue'
import AuthService from '@/services/authService.js'
// import UserService from '@/services/userService.js'
import config from '@/config'
import UserMainService from '@/services/userMainService.js'

export default {
  name: 'KnowledgeGraph',
  components: {
    KnowledgeGraphCanvas,
    KnowledgeGraph3D,
    GraphToolbar,
    PropertyPanel,
    TopBar
  },
  setup() {
    const store = useKnowledgeGraphStore()
    const graphCanvas = ref(null)
    const graph3D = ref(null)
    const graphToolbar = ref(null)
    const showToolbar = ref(true)
    const showImportModal = ref(false)
    const showSettings = ref(false)
    const showHelp = ref(false)
    const importDataText = ref('')
    const isLoading = ref(false)
    const isFullscreen = ref(false)
    const is3DMode = ref(false)

    // 帮助页面折叠状态
    const openHelpSections = ref({
      interface: true,
      toolbar: true
    })

    const openHelpSubsections = ref({
      interface: {
        search: false,
        expand: false,
        link: false,
        cutlink: false,
        group: false,
        other: false
      },
      toolbar: {
        findNode: false,
        node: false,
        relationship: false,
        addNewNode: false,
        addNode: false,
        editNode: false,
        deleteNode: false,
        addRelationship: false,
        deleteRelationship: false
      },
      base: {
        tag: true,
        bindTag: true,
        graph: true,
        node: true,
        graphData: true
      }
    })

    // 图形设置
    const graphSettings = reactive({
      draggable: true,
      selectable: true,
      zoomable: true,
      boxSelection: true,
      layoutType: 'force',
      treeDirection: 'left-to-right', // 添加树形布局方向配置
      autoLayout: true,
      showLabels: true,
      showImages: true,
      nodeSize: 30,
      // 关系线样式设置
      edgeStyle: 'straight',
      edgeColorScheme: 'default',
      edgeWidth: 2,
      showArrow: true,
      arrowStyle: 'default',
      enableGlow: false,
      enableAnimation: false
    })

    // 响应式数据
    const user = ref({
      name: '用户名',
      id: 1,
      userType: 1,
      avatar: require('@/assets/logo.png')
    })
    const apps = ref([])
    // 计算属性
    const selectedNode = computed(() => store.selectedNode)
    const selectedEdge = computed(() => store.selectedEdge)
    const groups = computed(() => store.groups)
    const tags = computed(() => store.tags)
    const selectedTags = computed(() => store.selectedTags)

    // 加载用户信息
    const loadUserInfo = () => {
      const currentUser = AuthService.getCurrentUser()
      if (currentUser) {
        console.log('当前用户信息:', currentUser);
        user.value.name = currentUser.nickname || currentUser.username || '用户名';
        user.value.id = currentUser.userid || currentUser.id || 1;
        user.value.userType = currentUser.userType || 1;
        console.log("icon:", currentUser.icon);
        // 如果有头像信息，则更新头像路径
        if (currentUser.icon) {
          // 规范化路径分隔符，将反斜杠转换为正斜杠
          const normalizedIcon = currentUser.icon.replace(/\\/g, '/');
          console.log('规范化后的icon路径:', normalizedIcon);

          // 构建完整的后端服务器地址，确保头像从8000端口获取
          if (normalizedIcon.startsWith('http')) {
            console.log('完整URL:', normalizedIcon);
            // 如果已经是完整URL，则直接使用
            user.value.avatar = normalizedIcon;
          } else if (normalizedIcon.startsWith('/extra-files/uploads/')) {
            console.log('extra-files URL:', normalizedIcon);
            // 从后端服务器的8000端口获取头像
            user.value.avatar = `${config.API_BASE_URL}${normalizedIcon}`;
          } else if (normalizedIcon.startsWith('uploads/')) {
            console.log('uploads URL:', normalizedIcon);
            // 从后端服务器的8000端口获取头像
            //user.value.avatar = `${config.API_BASE_URL}/extra-files/${normalizedIcon}`;

            user.value.avatar = `${config.API_BASE_URL}/extra-files/${normalizedIcon}`;
            //查看地址：`${config.API_BASE_URL}/extra-files/${normalizedIcon}`;
            console.log('user.value.avatar', user.value.avatar);
          } else {
            console.log('img URL:', normalizedIcon);
            user.value.avatar = `${config.API_BASE_URL}/extra-files/uploads/${normalizedIcon}`;
          }
        } else {
          console.log('使用默认头像');
          // 使用默认头像
          user.value.avatar = require('@/assets/logo.png');
        }
      }
    }

    // 加载应用信息
    const loadAppInfo = async () => {
      try {
        const response = await UserMainService.getAllApps(user.value.userType)
        if (response.status === 200 && response.apps) {
          apps.value = response.apps.map(app => ({
            id: app.id,
            name: app.name,
            desc: app.desc,
            link: app.name === '知识图谱' ? '/graph' : app.link,
            icon: app.icon || 'code',
            used: app.used || false
          }))
        }
      } catch (error) {
        console.error('获取应用数据失败:', error)
        apps.value = [
          { id: 1, name: 'OJ练习', desc: '在线编程练习', link: '/CppOJ/', icon: 'code', used: true },
          { id: 2, name: '任务清单', desc: '待办事项管理', link: '/todo', icon: 'list', used: true },
          { id: 3, name: '时间线', desc: '时间记录管理', link: '/TimeLine/', icon: 'timeline', used: false },
          { id: 4, name: '知识图谱', desc: '知识关系展示', link: '/graph', icon: 'share', used: true },
          { id: 5, name: '快速抢答', desc: '抢答与记忆训练', link: '/abhs', icon: 'quiz', used: false },
          { id: 6, name: '文件管理', desc: '文件上传下载', link: '/FileManage/', icon: 'folder', used: false },
          { id: 7, name: '数据可视化', desc: '数据图表展示', link: '/DataVisual/', icon: 'bar_chart', used: false }
        ]
      }
    }
    // 处理节点点击
    const handleNodeClick = (node) => {
      console.log('handleNodeClick 被调用，节点信息:', node);
      console.log('当前是否为3D模式:', is3DMode.value);
      store.setSelectedNode(node)
      store.setSelectedEdge(null)

      // 调用API扩展图谱
      if (node.name && node.name.trim() !== '') {
        console.log('准备调用 findGraphByNode API:', node.name);
        store.findGraphByNode(node.name)
          .then(() => {
            console.log('节点扩展成功:', node.name);
            // 在API调用成功后，确保图形被刷新
            nextTick(() => {
              if (is3DMode.value && graph3D.value) {
                // 3D模式下刷新图
                graph3D.value.loadGraphData();
              } else if (graphCanvas.value) {
                // 2D模式下刷新图
                graphCanvas.value.refreshGraph();
              }
            });
          })
          .catch((error) => {
            console.error('扩展节点失败:', error);
          });
      } else {
        console.warn('节点名称无效，跳过API调用:', node.name);
      }

      // 在3D模式下，同步节点选择状态
      nextTick(() => {
        if (is3DMode.value && graph3D.value) {
          graph3D.value.selectNode(node.id)
        } else if (graphCanvas.value) {
          // 在2D模式下，可能需要高亮节点
          graphCanvas.value.highlightRelatedNodes(node.id)
        }
      })
    }

    // 处理边点击
    const handleEdgeClick = (edge) => {
      store.setSelectedEdge(edge)
      store.setSelectedNode(null)
    }

    // 处理画布点击
    const handleCanvasClick = () => {
      store.clearSelection()
    }

    // 处理节点拖拽
    const handleNodeDrag = (nodeId, position) => {
      store.updateNode(nodeId, {
        x: position.x,
        y: position.y
      })
    }

    // 添加节点
    const handleAddNode = (nodeData) => {
      store.addNode(nodeData)
      nextTick(() => {
        if (graphCanvas.value) {
          graphCanvas.value.refreshGraph()
        }
      })
    }

    // 添加边
    const handleAddEdge = (edgeData) => {
      store.addEdge(edgeData)
      nextTick(() => {
        if (graphCanvas.value) {
          graphCanvas.value.refreshGraph()
        }
      })
    }

    // 添加分组
    const handleAddGroup = (groupData) => {
      store.addGroup(groupData)
    }

    // 处理筛选变化
    const handleFilterChange = (filters) => {
      store.setFilters(filters)
      nextTick(() => {
        if (graphCanvas.value) {
          graphCanvas.value.refreshGraph()
          // 添加高亮显示匹配节点的逻辑
          if (filters.searchText && filters.searchText.trim() !== '') {
            graphCanvas.value.highlightMatchingNodes(filters.searchText)
          } else {
            graphCanvas.value.clearHighlight()
          }
        }
      })
    }

    // 处理搜索
    const handleSearch = (searchText) => {
      store.setFilters({ searchText })
      nextTick(() => {
        if (graphCanvas.value) {
          graphCanvas.value.refreshGraph()
          // 添加高亮显示匹配节点的逻辑
          if (searchText && searchText.trim() !== '') {
            graphCanvas.value.highlightMatchingNodes(searchText)
          } else {
            graphCanvas.value.clearHighlight()
          }
        }
      })
    }

    // 更新节点
    const handleUpdateNode = (nodeId, updates) => {
      store.updateNode(nodeId, updates)
      nextTick(() => {
        if (graphCanvas.value) {
          graphCanvas.value.refreshGraph()
        }
      })
      // 同时更新store中的selectedNode引用，确保PropertyPanel能正确响应
      if (store.selectedNode && store.selectedNode.id === nodeId) {
        const updatedNode = store.nodes.find(node => node.id === nodeId)
        if (updatedNode) {
          store.setSelectedNode(updatedNode)
        }
      }
    }

    // 更新边
    const handleUpdateEdge = (edgeId, updates) => {
      store.updateEdge(edgeId, updates)
      nextTick(() => {
        if (graphCanvas.value) {
          graphCanvas.value.refreshGraph()
        }
      })
    }

    // 删除节点
    const handleDeleteNode = (nodeId) => {
      if (confirm('确定要删除这个节点吗？相关的连接也会被删除。')) {
        store.deleteNode(nodeId)
        nextTick(() => {
          if (graphCanvas.value) {
            graphCanvas.value.refreshGraph()
          }
        })
      }
    }

    // 删除边
    const handleDeleteEdge = (edgeId) => {
      if (confirm('确定要删除这条连接吗？')) {
        store.deleteEdge(edgeId)
        nextTick(() => {
          if (graphCanvas.value) {
            graphCanvas.value.refreshGraph()
          }
        })
      }
    }

    // 编辑节点
    const handleNodeEdit = (nodeId, newLabel) => {
      store.updateNode(nodeId, { label: newLabel })
      nextTick(() => {
        if (graphCanvas.value) {
          graphCanvas.value.refreshGraph()
        }
      })
    }

    // 编辑边
    const handleEdgeEdit = (edgeId, newLabel) => {
      store.updateEdge(edgeId, { label: newLabel })
      nextTick(() => {
        if (graphCanvas.value) {
          graphCanvas.value.refreshGraph()
        }
      })
    }

    // 处理添加关系事件
    const handleAddRelationship = async ({ source, target }) => {
      // 检查GraphToolbar中是否已选择关系类型
      if (graphToolbar.value && !graphToolbar.value.selectedGraphTypeId) {
        alert('请先在工具栏中选择关系类型')
        return
      }

      try {
        // 调用store中的方法添加关系
        const response = await store.addGraph(source.id, target.id, graphToolbar.value.selectedGraphTypeId)
        console.log('添加节点关系成功:', response)

        // 刷新图形
        // nextTick(() => {
        //   if (graphCanvas.value) {
        //     graphCanvas.value.refreshGraph()
        //   }
        // })

        // alert('节点关系添加成功')
      } catch (error) {
        console.error('添加节点关系失败:', error)
        alert('添加节点关系失败: ' + error.message)
      }
    }

    // 锁定节点
    const handleNodeLock = (nodeId) => {
      store.updateNode(nodeId, { locked: true })
      nextTick(() => {
        if (graphCanvas.value) {
          graphCanvas.value.refreshGraph()
        }
      })
    }

    // 高亮节点
    const handleNodeHighlight = (nodeId) => {
      if (graphCanvas.value) {
        graphCanvas.value.highlightRelatedNodes(nodeId)
      }
    }

    // 保存节点位置
    const handleSaveNodePosition = (nodeId, position) => {
      store.updateNode(nodeId, position)
      nextTick(() => {
        if (graphCanvas.value) {
          graphCanvas.value.refreshGraph()
        }
      })
    }

    // 重置布局
    const resetLayout = () => {
      if (is3DMode.value && graph3D.value) {
        graph3D.value.resetLayout()
      } else if (graphCanvas.value) {
        graphCanvas.value.resetLayout()
      }
    }

    // 适应视图
    const fitView = () => {
      if (is3DMode.value && graph3D.value) {
        graph3D.value.fitView()
      } else if (graphCanvas.value) {
        graphCanvas.value.fitView()
      }
    }

    // 放大
    const zoomIn = () => {
      if (is3DMode.value && graph3D.value) {
        graph3D.value.zoomIn()
      } else if (graphCanvas.value) {
        graphCanvas.value.zoomIn()
      }
    }

    // 缩小
    const zoomOut = () => {
      if (is3DMode.value && graph3D.value) {
        graph3D.value.zoomOut()
      } else if (graphCanvas.value) {
        graphCanvas.value.zoomOut()
      }
    }

    // 全屏切换
    const toggleFullscreen = () => {
      isFullscreen.value = !isFullscreen.value
      if (is3DMode.value && graph3D.value) {
        graph3D.value.toggleFullscreen && graph3D.value.toggleFullscreen()
      } else if (graphCanvas.value) {
        graphCanvas.value.toggleFullscreen && graphCanvas.value.toggleFullscreen()
      }
    }

    // 导出数据
    const exportData = () => {
      const data = store.exportData()
      const blob = new Blob([data], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `knowledge-graph-data-${new Date().toISOString().slice(0, 10)}.json`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    }

    // 确认导入数据
    const confirmImportData = () => {
      if (!importDataText.value.trim()) {
        alert('请输入要导入的数据')
        return
      }

      isLoading.value = true
      setTimeout(() => {
        try {
          const success = store.importData(importDataText.value)
          if (success) {
            showImportModal.value = false
            importDataText.value = ''
            nextTick(() => {
              if (graphCanvas.value) {
                graphCanvas.value.refreshGraph()
              }
            })
            alert('数据导入成功！')
          } else {
            alert('数据导入失败，请检查JSON格式是否正确')
          }
        } catch (error) {
          alert('数据导入失败：' + error.message)
        } finally {
          isLoading.value = false
        }
      }, 500)
    }

    // 应用设置
    const applySettings = () => {
      // 将设置应用到图形
      console.log('应用图形设置:', graphSettings)
      if (graphCanvas.value) {
        // 将布局类型设置传递给画布组件
        graphCanvas.value.setLayoutType(graphSettings.layoutType)
        // 将关系线样式设置传递给画布组件
        if (graphCanvas.value.setEdgeStyle) {
          graphCanvas.value.setEdgeStyle({
            style: graphSettings.edgeStyle,
            colorScheme: graphSettings.edgeColorScheme,
            width: graphSettings.edgeWidth,
            showArrow: graphSettings.showArrow,
            arrowStyle: graphSettings.arrowStyle,
            enableGlow: graphSettings.enableGlow,
            enableAnimation: graphSettings.enableAnimation
          })
        }
        // 刷新图形以应用新设置
        graphCanvas.value.refreshGraph()
      }

      // 更新store中的布局配置
      store.layoutConfig.type = graphSettings.layoutType
      if (graphSettings.layoutType === 'tree') {
        store.layoutConfig.treeDirection = graphSettings.treeDirection
      }

      showSettings.value = false
    }

    // 清空所有数据
    const clearAllData = () => {
      if (confirm('确定要清空所有数据吗？此操作不可恢复。')) {
        store.clearData()
        nextTick(() => {
          if (graphCanvas.value) {
            graphCanvas.value.refreshGraph()
          }
        })
      }
    }

    // 初始化应用
    const initApp = async () => {
      try {
        console.log('知识图谱系统初始化中...')

        // 加载用户信息和应用信息
        loadUserInfo()
        await loadAppInfo()

        // 加载图谱数据
        // await loadGraphData()

        console.log('知识图谱系统初始化完成')
      } catch (error) {
        console.error('知识图谱系统初始化失败:', error)
        // error.value = true
        // errorMessage.value = '系统初始化失败: ' + (error.message || '未知错误')
      } finally {
        isLoading.value = false
      }
    }

    // 加载标签数据
    const loadTags = async () => {
      try {
        console.log('加载标签数据...')
        const result = await store.findTags()
        console.log('标签数据加载完成，返回结果:', result)
        console.log('store中的标签数据:', store.tags)

        // 确保在下次DOM更新后初始化标签显示
        nextTick(() => {
          if (graphCanvas.value) {
            console.log('调用graphCanvas.initTags()')
            graphCanvas.value.initTags()
          } else {
            console.warn('graphCanvas.value为空，无法初始化标签显示')
          }
        })

        return result
      } catch (error) {
        console.error('加载标签数据失败:', error)
        throw error
      }
    }

    // 切换帮助章节的展开/折叠状态
    const toggleHelpSection = (section) => {
      openHelpSections.value[section] = !openHelpSections.value[section]
    }

    // 切换帮助子章节的展开/折叠状态
    const toggleHelpSubsection = (section, subsection) => {
      if (!openHelpSubsections.value[section]) {
        openHelpSubsections.value[section] = {}
      }
      openHelpSubsections.value[section][subsection] = !openHelpSubsections.value[section][subsection]
    }

    // 切换2D/3D视图模式
    const toggleViewMode = () => {
      is3DMode.value = !is3DMode.value
      console.log('切换视图模式:', is3DMode.value ? '3D' : '2D')

      // 在切换后调整视图
      nextTick(() => {
        if (is3DMode.value && graph3D.value) {
          // 切换到3D模式时，调用3D组件的适应视图方法
          graph3D.value.fitView()
        } else if (graphCanvas.value) {
          // 切换到2D模式时，调用2D组件的适应视图方法
          graphCanvas.value.fitView()
        }
      })
    }

    // 生命周期
    onMounted(() => {
      initApp()
      loadTags().then(() => {
        console.log('标签数据加载完成后的回调')
        store.loadData()

        // 延迟初始化标签显示，确保组件已挂载
        setTimeout(() => {
          console.log('延迟初始化标签显示')
          console.log('store中的标签数据:', store.tags)
          if (graphCanvas.value) {
            graphCanvas.value.initTags()
          }
        }, 200)
      }).catch(error => {
        console.error('加载标签数据失败:', error)
      })
      console.log('知识图谱系统组件已挂载')
    })

    return {
      graphCanvas,
      graph3D,
      graphToolbar,
      showToolbar,
      showImportModal,
      showSettings,
      showHelp,
      importDataText,
      isLoading,
      isFullscreen,
      is3DMode,
      selectedNode,
      selectedEdge,
      groups,
      tags,
      selectedTags,
      graphSettings,
      handleNodeClick,
      handleEdgeClick,
      handleCanvasClick,
      handleNodeDrag,
      handleAddNode,
      handleAddEdge,
      handleAddGroup,
      handleFilterChange,
      handleSearch,
      handleUpdateNode,
      handleUpdateEdge,
      handleDeleteNode,
      handleDeleteEdge,
      handleNodeEdit,
      handleEdgeEdit,
      handleNodeLock,
      handleNodeHighlight,
      handleSaveNodePosition,
      handleAddRelationship,
      resetLayout,
      fitView,
      zoomIn,
      zoomOut,
      toggleFullscreen,
      toggleViewMode,
      exportData,
      confirmImportData,
      applySettings,
      clearAllData,
      user,
      loadTags,
      // 帮助页面相关
      openHelpSections,
      openHelpSubsections,
      toggleHelpSection,
      toggleHelpSubsection
    }
  }
}
</script>

<style scoped>
.knowledge-graph {
  height: 98vh;
  display: flex;
  flex-direction: column;
  background-color: #f5f7fa;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  background-color: #ffffff;
  border-bottom: 1px solid #e1e5eb;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  z-index: 10;
}

.page-header h1 {
  margin: 0;
  font-size: 24px;
  font-weight: 600;
  color: #2d3748;
}

.header-actions {
  display: flex;
  gap: 12px;
}

.main-content {
  display: flex;
  flex: 1;
  overflow: hidden;
}

.sidebar {
  background-color: #ffffff;
  border: 1px solid #e1e5eb;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  z-index: 5;
  overflow-y: auto;
}

.left-sidebar {
  width: 280px;
  border-right: 1px solid #e1e5eb;
}

.right-sidebar {
  width: 320px;
  border-left: 1px solid #e1e5eb;
}

.main-area {
  flex: 1;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
}

.graph-container-wrapper {
  flex: 1;
  position: relative;
  overflow: hidden;
}

.graph-controls {
  position: absolute;
  top: 20px;
  right: 20px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  z-index: 100;
}

.control-btn {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #ffffff;
  border: 1px solid #e1e5eb;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  transition: all 0.2s ease;
}

.control-btn:hover {
  background-color: #f7fafc;
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

/* 按钮样式 */
.btn {
  padding: 8px 16px;
  border-radius: 4px;
  border: none;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;
}

.btn-secondary {
  background-color: #e2e8f0;
  color: #4a5568;
}

.btn-secondary:hover {
  background-color: #cbd5e0;
}

.btn-primary {
  background-color: #3182ce;
  color: white;
}

.btn-primary:hover {
  background-color: #2c5282;
}

.btn-outline {
  background-color: transparent;
  border: 1px solid #3182ce;
  color: #3182ce;
}

.btn-outline:hover {
  background-color: #ebf8ff;
}

.btn-danger {
  background-color: #e53e3e;
  color: white;
}

.btn-danger:hover {
  background-color: #c53030;
}

.btn-info {
  background-color: #38b2ac;
  color: white;
}

.btn-info:hover {
  background-color: #319795;
}

/* 模态框样式 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  width: 90%;
  max-width: 600px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
}

.settings-modal {
  max-width: 500px;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #e1e5eb;
}

.modal-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #718096;
}

.close-btn:hover {
  color: #4a5568;
}

.modal-body {
  padding: 20px;
  flex: 1;
  overflow-y: auto;
}

.settings-group {
  margin-bottom: 20px;
}

.settings-group h4 {
  margin-top: 0;
  margin-bottom: 12px;
  color: #2d3748;
  border-bottom: 1px solid #e1e5eb;
  padding-bottom: 8px;
}

.setting-item {
  margin-bottom: 12px;
}

.setting-item label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #4a5568;
  cursor: pointer;
}

.setting-item input[type="checkbox"] {
  width: 16px;
  height: 16px;
}

.setting-item select,
.setting-item input[type="range"] {
  margin-left: 8px;
  padding: 4px 8px;
  border: 1px solid #e1e5eb;
  border-radius: 4px;
}

.import-textarea {
  width: 100%;
  padding: 12px;
  border: 1px solid #e1e5eb;
  border-radius: 4px;
  font-family: monospace;
  font-size: 14px;
  resize: vertical;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 20px;
  border-top: 1px solid #e1e5eb;
}

/* 帮助弹窗样式 */
.help-overlay {
  background-color: rgba(0, 0, 0, 0.7);
}

.help-modal {
  max-width: 700px;
  width: 90%;
  max-height: 80vh;
}

.help-content {
  padding: 20px;
}

.help-section {
  margin-bottom: 15px;
  border: 1px solid #e1e5eb;
  border-radius: 4px;
}

.help-section-header {
  padding: 12px 15px;
  background-color: #f8f9fa;
  border-bottom: 1px solid #e1e5eb;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: 500;
}

.help-section-header:hover {
  background-color: #e9ecef;
}

.help-section-content {
  padding: 10px 15px;
}

.help-subsection {
  margin-bottom: 10px;
}

.help-subsection-header {
  padding: 8px 10px;
  background-color: #ffffff;
  border: 1px solid #e1e5eb;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: 500;
}

.help-subsection-header:hover {
  background-color: #f8f9fa;
}

.help-subsection-content {
  padding: 10px;
  margin-top: 5px;
  background-color: #f8f9fa;
  border-radius: 4px;
}

.help-toggle-icon {
  font-weight: bold;
  font-size: 18px;
}

.help-section h4 {
  margin: 0;
  color: #2d3748;
}

.help-section h5,
.help-section h6 {
  margin: 0;
  color: #4a5568;
}

.help-section ul {
  padding-left: 20px;
  margin: 10px 0;
}

.help-section li {
  margin-bottom: 8px;
  line-height: 1.5;
}

/* 关系数据显示样式 */
.relation-display {
  list-style: none;
  padding: 0;
  margin: 10px 0;
}

.relation-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  background-color: #f8f9fa;
  border-radius: 6px;
  border: 1px solid #e9ecef;
}

.relation-label {
  font-weight: bold;
  font-size: 16px;
  color: #495057;
}

.relation-structure {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  padding: 8px 12px;
  background-color: #ffffff;
  border-radius: 4px;
  border: 1px solid #dee2e6;
}

.slot {
  padding: 6px 12px;
  border-radius: 4px;
  font-family: monospace;
  font-size: 14px;
  font-weight: 500;
}

.from-node {
  background-color: #e9ecef;
  border: 1px solid #ced4da;
}

.graph {
  background-color: #e9ecef;
  border: 1px solid #ced4da;
}

.to-node {
  background-color: #e9ecef;
  border: 1px solid #ced4da;
}

.connector {
  font-weight: bold;
  color: #6c757d;
}

/* 不同值的颜色 */
.null-value {
  background-color: #e9ecef !important;
  color: #6c757d;
  border-color: #ced4da !important;
}

.node-value {
  background-color: #d1ecf1 !important;
  color: #0c5460;
  border-color: #bee5eb !important;
}

.tag-value {
  background-color: #d4edda !important;
  color: #155724;
  border-color: #c3e6cb !important;
}

/* 加载提示样式 */
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
}

.loading-spinner {
  text-align: center;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #e2e8f0;
  border-top: 4px solid #3182ce;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 16px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

/* 响应式设计 */
@media (max-width: 1200px) {
  .left-sidebar {
    width: 240px;
  }

  .right-sidebar {
    width: 280px;
  }
}

@media (max-width: 992px) {
  .left-sidebar {
    width: 200px;
  }

  .right-sidebar {
    width: 240px;
  }
}

@media (max-width: 768px) {
  .page-header {
    flex-direction: column;
    gap: 16px;
    text-align: center;
  }

  .header-actions {
    flex-wrap: wrap;
    justify-content: center;
  }

  .left-sidebar,
  .right-sidebar {
    position: absolute;
    top: 0;
    bottom: 0;
    z-index: 100;
    box-shadow: 2px 0 10px rgba(0, 0, 0, 0.1);
  }

  .left-sidebar {
    left: 0;
    transform: translateX(-100%);
    transition: transform 0.3s ease;
  }

  .left-sidebar.show {
    transform: translateX(0);
  }

  .right-sidebar {
    right: 0;
    transform: translateX(100%);
    transition: transform 0.3s ease;
  }

  .right-sidebar.show {
    transform: translateX(0);
  }
}
</style>